import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Button, CircularProgress, Alert } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { collection, query, where, getDocs, getDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Check as CheckIcon, Error as ErrorIcon, Print as PrintIcon } from '@mui/icons-material';

/**
 * PrintProxy component
 * This page handles printer requests that come from QR codes
 * It extracts the job ID and printer ID from URL parameters,
 * fetches the print data, and sends it to the printer
 */
const PrintProxy: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'ready' | 'printing' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [printerName, setPrinterName] = useState<string>('');
  const [printerAddress, setPrinterAddress] = useState<string>('');
  const [printData, setPrintData] = useState<string>('');

  // Get the parameters from the URL
  const jobId = searchParams.get('job');
  const printerId = searchParams.get('printer');
  const useLocalStorage = searchParams.get('local') === 'true';
  const directAddress = searchParams.get('address');
  
  useEffect(() => {
    // Validate parameters
    if (!jobId || !printerId) {
      setStatus('error');
      setErrorMessage('Missing required parameters');
      return;
    }

    // Load the print job data
    if (useLocalStorage) {
      loadPrintJobFromLocalStorage();
    } else {
      loadPrintJobFromFirestore();
    }
  }, [jobId, printerId, useLocalStorage, directAddress]);

  const loadPrintJobFromLocalStorage = () => {
    try {
      // Get the print job from localStorage
      const printJobStr = localStorage.getItem(`print_job_${jobId}`);
      
      if (!printJobStr) {
        setStatus('error');
        setErrorMessage('Print job not found or expired');
        return;
      }
      
      const printJob = JSON.parse(printJobStr);
      
      // Get printer information from localStorage or use default
      if (directAddress) {
        setPrinterName('Network Printer');
        setPrinterAddress(directAddress);
        setPrintData(printJob.data);
        setStatus('ready');
      } else {
        // Look for stored printer info
        const storedPrinters = JSON.parse(localStorage.getItem('printers') || '[]');
        const printer = storedPrinters.find((p: any) => p.id === printerId);
        
        if (!printer) {
          setStatus('error');
          setErrorMessage('Printer not found');
          return;
        }
        
        setPrinterName(printer.name || 'Printer');
        setPrinterAddress(printer.address);
        setPrintData(printJob.data);
        setStatus('ready');
      }
    } catch (error) {
      console.error('Error loading print job from localStorage:', error);
      setStatus('error');
      setErrorMessage(`Error loading print job: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const loadPrintJobFromFirestore = async () => {
    try {
      // Get the print job
      const printJobsRef = collection(db, 'printJobs');
      const q = query(printJobsRef, where('jobId', '==', jobId));
      const printJobsSnapshot = await getDocs(q);

      if (printJobsSnapshot.empty) {
        setStatus('error');
        setErrorMessage('Print job not found or expired');
        return;
      }

      // Get the print data
      const printDataRef = collection(db, 'printData');
      const dataQ = query(printDataRef, where('jobId', '==', jobId));
      const printDataSnapshot = await getDocs(dataQ);

      if (printDataSnapshot.empty) {
        setStatus('error');
        setErrorMessage('Print data not found');
        return;
      }

      // Get the printer
      const printerRef = doc(db, 'printers', printerId);
      const printerSnapshot = await getDoc(printerRef);

      if (!printerSnapshot.exists()) {
        setStatus('error');
        setErrorMessage('Printer not found');
        return;
      }

      const printerData = printerSnapshot.data();
      setPrinterName(printerData.name || 'Printer');
      setPrinterAddress(printerData.address);
      setPrintData(printDataSnapshot.docs[0].data().data);

      // Ready to print
      setStatus('ready');
    } catch (error) {
      console.error('Error loading print job from Firestore:', error);
      setStatus('error');
      setErrorMessage(`Error loading print job: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handlePrint = async () => {
    try {
      setStatus('printing');

      if (!printerAddress || !printData) {
        throw new Error('Printer address or print data not found');
      }

      // Try different common printer endpoints
      const endpoints = [
        '/printer/print',
        '/pstprnt',
        '/zpl',
        '/zebra/print'
      ];
      
      // Use no-cors mode to bypass CORS issues
      const fetchOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `data=${encodeURIComponent(printData)}`,
        mode: 'no-cors' as RequestMode
      };
      
      let success = false;
      
      // Try each endpoint
      for (const endpoint of endpoints) {
        try {
          console.log(`Trying to print to ${printerAddress}${endpoint}...`);
          await fetch(`http://${printerAddress}${endpoint}`, fetchOptions);
          success = true;
          console.log(`Successfully sent to ${endpoint} (assuming success due to no-cors)`);
          break;
        } catch (endpointError) {
          console.warn(`Failed to print to ${endpoint}:`, endpointError);
          // Continue to next endpoint
        }
      }
      
      if (!success) {
        throw new Error('Failed to print to any printer endpoint');
      }

      // Update job status if using Firestore
      if (!useLocalStorage) {
        try {
          const printDataRef = collection(db, 'printData');
          const dataQ = query(printDataRef, where('jobId', '==', jobId));
          const printDataSnapshot = await getDocs(dataQ);
          
          if (!printDataSnapshot.empty) {
            const printJobRef = doc(db, 'printJobs', printDataSnapshot.docs[0].id);
            await updateDoc(printJobRef, {
              status: 'completed',
            });
          }
        } catch (updateError) {
          console.warn('Failed to update job status in Firestore:', updateError);
        }
      } else {
        // Clean up localStorage if needed
        try {
          localStorage.removeItem(`print_job_${jobId}`);
        } catch (localStorageError) {
          console.warn('Failed to clean up localStorage:', localStorageError);
        }
      }

      setStatus('success');
    } catch (error) {
      console.error('Error printing:', error);
      setStatus('error');
      setErrorMessage(`Error printing: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      p: 2
    }}>
      <Paper sx={{ 
        maxWidth: 500, 
        width: '100%', 
        p: 4, 
        textAlign: 'center',
        borderRadius: 2,
        boxShadow: 3
      }}>
        <Typography variant="h5" gutterBottom>
          Fallen Leaf Marina Print Service
        </Typography>

        {status === 'loading' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>Loading print job...</Typography>
          </Box>
        )}

        {status === 'printing' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>Sending to printer...</Typography>
          </Box>
        )}

        {status === 'ready' && (
          <Box sx={{ py: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Print job ready to send to <strong>{printerName}</strong>
              {printerAddress && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Printer address: {printerAddress}
                </Typography>
              )}
            </Alert>
            <Typography paragraph>
              This device must be on the same local network as the printer.
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              size="large"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{ mt: 2 }}
            >
              Print Now
            </Button>
          </Box>
        )}

        {status === 'success' && (
          <Box sx={{ py: 2 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: 'success.main',
              mb: 2
            }}>
              <CheckIcon sx={{ fontSize: 60 }} />
            </Box>
            <Typography variant="h6" gutterBottom>
              Print job sent successfully!
            </Typography>
            <Typography paragraph>
              Your document should be printing on {printerName} now.
            </Typography>
            <Button 
              variant="outlined" 
              color="primary"
              onClick={() => window.close()}
              sx={{ mt: 2 }}
            >
              Close
            </Button>
          </Box>
        )}

        {status === 'error' && (
          <Box sx={{ py: 2 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: 'error.main',
              mb: 2
            }}>
              <ErrorIcon sx={{ fontSize: 60 }} />
            </Box>
            <Typography variant="h6" gutterBottom>
              Printing Error
            </Typography>
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage || 'An unknown error occurred'}
            </Alert>
            <Typography paragraph>
              Please try again or contact support if the issue persists.
            </Typography>
            <Button 
              variant="outlined" 
              color="primary"
              onClick={() => window.close()}
              sx={{ mt: 2 }}
            >
              Close
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default PrintProxy; 